/**
 * 查新 AI 搜索统一 API 接口定义
 * 对应 Python 服务 novelty_controller 中的各接口
 */

import type {
    SelectedComponent,
    TrimmedImage,
    AsyncTaskData,
    ComponentBindLocateRequest,
    UpdateComponentBindLocateRequest,
    ComponentBindLocateResponseData,
} from './component-bindlocate-api';

// ==================== component_extract_bindlocate ====================

/**
 * 部件提取+定位组合接口 - 图片项
 */
export interface ComponentExtractBindLocateImageItem {
    /** 图片ID */
    image_id: string;
    /** 图片URL */
    url: string;
}

/**
 * 原始特征项（含部件）
 */
export interface OriginFeatureItem {
    /** 部件列表 */
    components: string[];
    /** 特征文本 */
    feature_text: string;
}

/**
 * 部件提取+定位组合接口 - 请求体
 */
export interface ComponentExtractBindLocateRequest {
    /** 任务ID */
    task_id: string;
    /** 专利ID */
    patent_id: string;
    /** 本发明原始特征列表（含部件） */
    origin_features: OriginFeatureItem[];
    /** 对比专利特征文本列表 */
    comparison_features: string[];
    /** 图片列表 */
    images: ComponentExtractBindLocateImageItem[];
    /** 语言 */
    language?: string;
    is_async: boolean;
}

/**
 * 特征对比结果中的单个项
 */
export interface ExtractResultItem {
    /** 特征编号 */
    feature_num: string;
    /** 原始特征文本 */
    origin_feature: string;
    /** 原始部件 */
    origin_component: string;
    /** 对比特征文本 */
    compare_feature: string;
    /** 对比部件 */
    compare_component: string;
    /** 差异描述 */
    differ: string;
}

/**
 * 部件提取结果
 */
export interface ComponentExtractResult {
    /** 特征对比项列表 */
    items: ExtractResultItem[];
}

/**
 * 定位结果
 */
export interface ComponentBindLocateResult {
    /** 选中的部件列表 */
    selected_components: SelectedComponent[];
    /** 最终使用的附图ID列表 */
    final_figs_used: string[];
}

/**
 * 部件提取+定位组合接口 - 响应数据
 */
export interface ComponentExtractBindLocateResponseData {
    /** 部件提取结果 */
    extract_result: ComponentExtractResult;
    /** 定位结果（提取无部件时为 null） */
    bindlocate_result: ComponentBindLocateResult | null;
    /** 裁剪白边后的图片映射（image_id → 图片信息，上传失败时为 null） */
    trimmed_images: Record<string, TrimmedImage | null>;
}

// ==================== task stream ====================

export type NoveltyAiSearchStreamEventType =
    | 'task_start'
    | 'task_complete'
    | 'task_error'
    | 'group_start'
    | 'group_complete'
    | 'group_skip'
    | 'tool_start'
    | 'tool_cache_hit'
    | 'tool_complete'
    | 'tool_error'
    | 'llm_start'
    | 'llm_delta'
    | 'llm_end'
    | 'progress';

export interface NoveltyAiSearchStreamRequest {
    /** 任务ID */
    taskId: string;
    /** 断线重连时传入上次收到的最后事件 ID */
    lastEventId?: string | number;
}

export interface NoveltyAiSearchStreamEventPayload {
    /** 当前进度/LLM 消息关联的专利 ID */
    patent_id?: string;
    /** 当前进度/LLM 消息关联的专利公开号 */
    pn?: string;
    [key: string]: unknown;
}

export interface NoveltyAiSearchStreamEvent {
    /** SSE data 中的事件类型 */
    event_type: NoveltyAiSearchStreamEventType | string;
    /** SSE event 字段 */
    event?: string;
    /** SSE id 字段 */
    event_id?: string;
    group_id?: string;
    group_name?: string;
    step_name?: string;
    step_index?: number;
    total_steps?: number;
    strategy_type?: string;
    tool_id?: string;
    tool_name?: string;
    name?: string;
    message?: string;
    model?: string;
    delta?: string;
    cost?: number;
    target_key?: string;
    event_payload?: NoveltyAiSearchStreamEventPayload;
    error_message?: string;
    error?: string;
    error_code?: string | number;
    [key: string]: unknown;
}

export interface NoveltyAiSearchTaskStream {
    onOpen(callback: (response: Response) => void): this;
    onMessage(callback: (data: NoveltyAiSearchStreamEvent) => void): this;
    onClose(callback: () => void): this;
    onError(callback: (err: any) => void): this;
    onFinish(callback: () => void): this;
    onParseError(callback: (error: any) => void): this;
    close(): void;
    start(): Promise<void>;
}

// ==================== API 接口 ====================

/**
 * 查新 AI 搜索统一 API
 */
export const NoveltyAiSearchApi = Symbol.for('NoveltyAiSearchApi');

export interface NoveltyAiSearchApi {
    /**
     * 部件提取 + 图片定位组合接口
     * @param data 请求数据
     * @returns 异步任务数据
     */
    componentExtractBindLocate(data: ComponentExtractBindLocateRequest): Promise<AsyncTaskData<ComponentExtractBindLocateResponseData>>;

    /**
     * 部件绑定定位
     * @param data 请求数据
     * @returns 异步任务数据
     */
    componentBindLocate(data: ComponentBindLocateRequest): Promise<AsyncTaskData<ComponentBindLocateResponseData>>;

    /**
     * 更新部件绑定定位（保存用户标注修改）
     * @param data 更新请求数据
     * @returns 更新后的定位结果
     */
    updateComponentBindLocate(data: UpdateComponentBindLocateRequest): Promise<ComponentBindLocateResponseData>;

    /**
     * 订阅任务流式事件
     * @param data 流请求参数
     * @returns SSE 流对象
     */
    getTaskStream(data: NoveltyAiSearchStreamRequest): Promise<NoveltyAiSearchTaskStream>;
}
